import React, { useState, useEffect } from 'react';
import { X, ExternalLink } from 'lucide-react';
import getFullUrl from '../../utils/formatUrl';

const AnnouncementPopup = ({ data = {} }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const isPopupEnabled = data.popup_enabled === 'true';
        if (!isPopupEnabled) return;

        // Check if popup was already closed in this session
        const hasSeenPopup = sessionStorage.getItem('as_popup_dismissed');
        
        if (!hasSeenPopup) {
            const timer = setTimeout(() => {
                setIsOpen(true);
                setTimeout(() => setIsVisible(true), 50); // For entry animation
            }, 1500); // 1.5s delay for better UX
            return () => clearTimeout(timer);
        }
    }, [data.popup_enabled]);

    const handleClose = () => {
        setIsVisible(false);
        setTimeout(() => {
            setIsOpen(false);
            sessionStorage.setItem('as_popup_dismissed', 'true');
        }, 500); // Duration for fadeOut
    };

    if (!isOpen) return null;

    const imageUrl = getFullUrl(data.popup_image);

    return (
        <div 
            className={`fixed inset-0 z-[100] flex items-center justify-center p-4 transition-all duration-500 ease-in-out ${isVisible ? 'bg-slate-900/60 backdrop-blur-sm' : 'bg-transparent pointer-events-none'}`}
            onClick={handleClose}
        >
            <div 
                className={`bg-white w-full max-w-2xl rounded-3xl overflow-hidden shadow-[0_50px_100px_-20px_rgba(0,0,0,0.5)] relative transition-all duration-500 transform ${isVisible ? 'scale-100 translate-y-0 opacity-100' : 'scale-90 translate-y-12 opacity-0'}`}
                onClick={e => e.stopPropagation()}
            >
                {/* Close Button - Moved further into corner and made more distinct */}
                <button 
                    onClick={handleClose}
                    className="absolute top-4 right-4 z-50 w-10 h-10 bg-white/80 backdrop-blur-md rounded-full flex items-center justify-center text-slate-900 hover:bg-[#cc0000] hover:text-white shadow-lg transition-all border border-slate-100 group hover:scale-110"
                >
                    <X size={20} className="group-hover:rotate-90 transition-transform duration-500" />
                </button>

                <div className="flex flex-col md:flex-row h-full">
                    {/* Image Side - If exists */}
                    {imageUrl && (
                        <div className="md:w-1/2 relative bg-slate-900 overflow-hidden min-h-[250px] md:min-h-[400px]">
                            <img 
                                src={imageUrl} 
                                className="w-full h-full object-cover opacity-95 group-hover:scale-110 transition-transform duration-[2000ms]" 
                                alt="Announcement" 
                            />
                        </div>
                    )}

                    {/* Content Side */}
                    <div className={`p-8 md:p-12 pb-10 flex flex-col justify-center ${imageUrl ? 'md:w-1/2' : 'w-full text-center'}`}>
                        <div className={`mb-6 mt-4 md:mt-0 ${imageUrl ? 'text-left' : 'flex flex-col items-center'}`}>
                            <span className="px-5 py-2 bg-[#cc0000]/10 text-[#cc0000] text-[9px] font-black uppercase tracking-[4px] rounded-full inline-block border border-[#cc0000]/5">
                                Latest News
                            </span>
                        </div>

                        <h2 className={`text-3xl md:text-4xl font-black text-slate-900 uppercase tracking-tighter leading-tight mb-6 ${imageUrl ? 'text-left' : 'text-center'}`}>
                            {data.popup_title || 'Important Notice'}
                        </h2>

                        <p className={`text-slate-500 text-sm leading-relaxed mb-10 font-medium ${imageUrl ? 'text-left' : 'text-center'}`}>
                            {data.popup_message || 'Stay updated with the latest news and admissions at Arya Sabhyata.'}
                        </p>

                        <div className={`flex flex-col gap-4 ${imageUrl ? 'items-start' : 'items-center'}`}>
                            {data.popup_link && (
                                <a 
                                    href={data.popup_link} 
                                    className="w-full md:w-auto px-10 py-5 bg-slate-900 text-white font-black uppercase text-[11px] tracking-[5px] hover:bg-[#cc0000] shadow-2xl transition-all rounded-sm flex items-center justify-center group"
                                    onClick={handleClose}
                                >
                                    {data.popup_btn_text || 'See More'}
                                    <ExternalLink size={14} className="ml-3 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                                </a>
                            )}
                            
                            <button 
                                onClick={handleClose}
                                className="text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-slate-900 transition-colors"
                            >
                                Dismiss Notice
                            </button>
                        </div>
                    </div>
                </div>

                {/* Decorative Accent */}
                <div className="absolute top-0 left-0 w-1.5 h-full bg-[#cc0000]"></div>
            </div>
        </div>
    );
};

export default AnnouncementPopup;
